import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, Title, Tooltip, Legend);

const ChartWrapper = styled.div`
  position: relative;
  background-color: #f9f9f9;
  width: 100%;
  height: 75vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  justify-content: center;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;

  &:hover {
    color: #000;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ZakupyParetoChart = ({ data }) => {
  const [purchaseData, setPurchaseData] = useState({});
  const [chartData, setChartData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("full");

  const calculatePurchaseValue = (row) =>
    parseFloat(row.K_42) || 0;

  useEffect(() => {
    if (!data || data.length === 0) return;

    const grouped = data.reduce((acc, row) => {
      const year = parseInt(row.Rok);
      const month = parseInt(row.Miesiac);
      const contractor = row.NazwaDostawcy;
      const value = calculatePurchaseValue(row);

      if (!acc[year]) acc[year] = {};
      if (!acc[year][month]) acc[year][month] = {};
      if (!acc[year][month][contractor]) acc[year][month][contractor] = 0;

      acc[year][month][contractor] += value;
      return acc;
    }, {});

    const years = Object.keys(grouped).map(Number).sort((a, b) => b - a);
    const latestYear = years[0];
    const months = Object.keys(grouped[latestYear]).map(Number).sort((a, b) => a - b);

    setPurchaseData(grouped);
    setAvailableYears(years);
    setAvailableMonths(months);
    setSelectedYear(latestYear);
    setSelectedMonth("full");
  }, [data]);

  useEffect(() => {
    if (!selectedYear || !purchaseData[selectedYear]) return;

    let contractorMap = {};

    if (selectedMonth === "full") {
      Object.values(purchaseData[selectedYear]).forEach((monthData) => {
        Object.entries(monthData).forEach(([name, val]) => {
          contractorMap[name] = (contractorMap[name] || 0) + val;
        });
      });
    } else {
      contractorMap = purchaseData[selectedYear][parseInt(selectedMonth)] || {};
    }

    const sorted = Object.entries(contractorMap)
      .sort(([, a], [, b]) => b - a)
      .map(([name, value]) => ({ name, value }));

    const total = sorted.reduce((sum, x) => sum + x.value, 0);

    let cumulative = 0;
    const paretoData = sorted.map((c) => {
      cumulative += c.value;
      return {
        ...c,
        cumulativePercentage: (cumulative / total) * 100,
      };
    });

    const filtered = paretoData.filter((x) => x.cumulativePercentage <= 80);

    setChartData({
      labels: filtered.map((x) => x.name),
      datasets: [
        {
          type: "bar",
          label: "Wartość Zakupów (PLN)",
          data: filtered.map((x) => x.value),
          backgroundColor: "#9848c7",
          borderColor: "#9848c7",
          borderWidth: 1,
        },
        {
          type: "line",
          label: "Kumulacyjny Procent (%)",
          data: filtered.map((x) => x.cumulativePercentage),
          borderColor: "#ff6384",
          borderWidth: 2,
          pointBackgroundColor: "#ff6384",
          yAxisID: "y1",
        },
      ],
    });
  }, [selectedYear, selectedMonth, purchaseData]);

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="zakupy-pareto-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="zakupy-pareto-tooltip"
        place="left"
        content={
          <>
            <strong>Pareto Zakupów</strong>
            <br />
            Pokazuje dostawców odpowiadających<br />
            za 80% łącznej wartości zakupów.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok:&nbsp;</label>
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Miesiąc:&nbsp;</label>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="full">Pełny rok</option>
            {availableMonths.map((m) => (
              <option key={m} value={m}>
                {new Date(0, m - 1).toLocaleString("pl-PL", { month: "long" })}
              </option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      {chartData && (
        <Chart
          type="bar"
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              title: {
                display: true,
                text: `Wykres Pareto– Top 80% Dostawców (${selectedYear}${selectedMonth !== "full" ? `/${selectedMonth}` : ""})`,
              },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    ctx.dataset.type === "bar"
                      ? `Zakupy: ${ctx.raw.toLocaleString("pl-PL")} PLN`
                      : `Kumulacyjnie: ${ctx.raw.toFixed(2)}%`,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Wartość zakupów (PLN)" },
                ticks: {
                  callback: (val) => val.toLocaleString("pl-PL"),
                },
              },
              y1: {
                position: "right",
                beginAtZero: true,
                max: 100,
                title: { display: true, text: "Kumulacyjny %" },
                ticks: {
                  callback: (val) => `${val}%`,
                },
              },
              x: {
                title: { display: true, text: "Dostawcy" },
                ticks: {
                  callback: (val) => {
                    const label = chartData.labels[val];
                    return label.length > 15 ? label.slice(0, 15) + "..." : label;
                  },
                },
              },
            },
          }}
        />
      )}
    </ChartWrapper>
  );
};

export default ZakupyParetoChart;
